import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import RollReveal from './RollReveal';
import { selectDie } from './dice';
import { activatePhaseAnchor } from './phaseAnchor';
import { recordThrowEvent } from './throwCombo';
import { activatePortalPair } from './portalPair';
import {
  beginLastStandRoll,
  commitLastStandRoll,
  getLastStandRollGame,
  updateLastStandRoll,
} from './lastStandProtocol';
import { flipRoll, getEffectiveRoll, getLastStandRoom } from './game';
import './LastStandProtocol.css';
import LastStandRescue from './LastStandRescue';
import { toggleChainReaction } from './chainReaction';

const LastStandProtocolRoll = ({ game, setGame, onResolve }) => {
  const panel = useRef(null);
  const heading = useRef(null);
  const pending = game.lastStandProtocol.pending;
  const restored = useRef(pending.stage === 'settled');
  const rollGame = getLastStandRollGame(game);
  const room = getLastStandRoom(game);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    heading.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected)
        previousFocus.focus?.({ preventScroll: true });
    };
  }, []);

  if (pending.stage === 'ready' && !pending.rescueSeen) {
    const updateStory = (changes) =>
      setGame((current) => {
        const active = current.lastStandProtocol?.pending;
        if (!active || active.encounter !== pending.encounter) return current;
        return {
          ...current,
          lastStandProtocol: {
            ...current.lastStandProtocol,
            pending: { ...active, ...changes },
          },
        };
      });
    return (
      <LastStandRescue
        dieId={pending.selectedDie || game.selectedDie}
        impactSeen={pending.impactSeen}
        reduceMotion={
          !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        }
        onImpact={() => updateStory({ impactSeen: true })}
        onContinue={() => updateStory({ rescueSeen: true })}
      />
    );
  }

  return createPortal(
    <section
      ref={panel}
      className="coreboundLastStand"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coreboundLastStandTitle"
      aria-describedby="coreboundLastStandHelp"
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const controls = [
          ...panel.current.querySelectorAll(
            'button:not(:disabled), [tabindex="0"]'
          ),
        ].filter((node) => node.getClientRects().length > 0);
        if (!controls.length) {
          event.preventDefault();
          return;
        }
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!controls.includes(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
    >
      <header className="coreboundLastStandHeader">
        <span>YOUR LAST STAND RELIC IS HOLDING YOU ALIVE</span>
        <h1 id="coreboundLastStandTitle" ref={heading} tabIndex={-1}>
          Last Stand Protocol
        </h1>
        <p id="coreboundLastStandHelp">
          Score <b>76 or higher</b> after tile and relic bonuses to return with{' '}
          <b>1 HP</b>. This is your one last chance.
        </p>
      </header>
      <div className="coreboundLastStandContent">
        <RollReveal
          onSelectDie={(id) =>
            setGame((current) =>
              updateLastStandRoll(current, (rollGame) =>
                selectDie(rollGame, id)
              )
            )
          }
          key={pending.stage === 'ready' ? 'setup' : 'throw'}
          game={rollGame}
          room={room}
          survival
          onToggleChainReaction={() => setGame(toggleChainReaction)}
          setup={pending.stage === 'ready'}
          effectiveRoll={getEffectiveRoll(rollGame, room)}
          restoreCompletedRoll={restored.current}
          rollLaunch={pending.launch}
          rollCharge={pending.launch?.power || 0.72}
          onStartRoll={(launch) =>
            setGame((current) => beginLastStandRoll(current, launch))
          }
          onRollComplete={(roll, index) =>
            setGame((current) => commitLastStandRoll(current, roll, index))
          }
          onBoardBump={() =>
            setGame((current) =>
              updateLastStandRoll(current, (rollGame) =>
                recordThrowEvent(rollGame, 'bump')
              )
            )
          }
          onPhaseAnchor={() =>
            setGame((current) =>
              updateLastStandRoll(current, activatePhaseAnchor)
            )
          }
          onPortalTransit={() =>
            setGame((current) =>
              updateLastStandRoll(current, activatePortalPair)
            )
          }
          onFlip={() =>
            setGame((current) => updateLastStandRoll(current, flipRoll))
          }
          onResolve={onResolve}
        />
      </div>
    </section>,
    document.body
  );
};

export default LastStandProtocolRoll;
